import { lt } from '@mews/framework';
import { ContentCard, DefaultErrorBoundary } from '@mews-commander/ui';
import { getServiceTimeUnitPluralKey } from '@mews-commander/legacy-entities/src/utils/serviceTimeUnitLocalization';
import { getTimeUnitByServiceId } from '@mews-commander/legacy-entities/src/ducks/services';
import { useCommanderSelector } from '@mews-commander/utils';
import { PAGINATION_SIZES, StandaloneTable, usePaginatedData, useTableState } from '@optimus-web/core';
import { OrderItem } from '@mews-commander/types';
import { TimeUnit } from '@mews/enums';
import { useIsMultiTaxEnvironment } from '@mews-commander/legacy-entities/src/enterprise';
// eslint-disable-next-line @mews/no-internal-package
import { FrameworkTextKey } from '@mews-framework/localization/src/FrameworkTextKey'; // < -- To be removed after the migration of getServiceTimeUnitPluralKey
import { getSortedAllDefaultProductOrderItems } from '../../../selectors/orderItems';
import { simpleColumns, TableMeta } from './TableDefinition';

interface ItemsNightsTableProps {
    serviceId: string;
    reservationId: string;
    groupId: string;
}

export const ItemsNightsTable: React.FC<ItemsNightsTableProps> = ({ serviceId, reservationId, groupId }) => {
    const timeUnitKey = useCommanderSelector((state) => getServiceTimeUnitPluralKey(state, { id: serviceId }));
    const timeUnit = useCommanderSelector((state) => getTimeUnitByServiceId(state, { serviceId }));
    const nights = useCommanderSelector((state) =>
        getSortedAllDefaultProductOrderItems(state, { serviceId, serviceOrderIds: [reservationId] })
    );
    const isMultiTaxEnvironment = useIsMultiTaxEnvironment();

    return (
        <ItemsNightsTableCore
            cardTitle={timeUnitKey}
            nights={nights || []}
            groupId={groupId}
            timeUnit={timeUnit}
            isMultiTaxEnvironment={isMultiTaxEnvironment}
        />
    );
};

interface ItemsNightsTableCoreProps {
    cardTitle?: FrameworkTextKey;
    nights: OrderItem[];
    groupId: string;
    timeUnit: TimeUnit;
    isMultiTaxEnvironment: boolean;
}

const ItemsNightsTableCore: React.FC<ItemsNightsTableCoreProps> = ({
    cardTitle,
    nights,
    groupId,
    timeUnit,
    isMultiTaxEnvironment,
}) => {
    const [tableState, setTableState] = useTableState<OrderItem>({
        pagination: {
            show: true,
            pageSize: PAGINATION_SIZES[25], // Default page size, can be adjusted as needed
        },
    });

    const { data: nightSlice, totalRowCount } = usePaginatedData(nights, tableState.pagination);

    return (
        <DefaultErrorBoundary>
            <ContentCard title={cardTitle && lt(cardTitle)} data-test-items-night-table>
                <StandaloneTable
                    uniqueRowId="id"
                    amountOfRows={totalRowCount}
                    data={nightSlice}
                    columns={simpleColumns}
                    state={tableState}
                    meta={
                        {
                            timeUnit,
                            groupId,
                            isMultiTaxEnvironment,
                        } satisfies TableMeta
                    }
                    onStateChange={setTableState}
                />
            </ContentCard>
        </DefaultErrorBoundary>
    );
};
